import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Checkbox,
  TextField,
  Typography,
  Switch,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import LinkIcon from '@mui/icons-material/Link';
import { Button, Stack } from '@mui/material';

const ParametersPanel = ({
  t,
  unitOptions,
  rankOptions,
  yearOptions,
  filters,
  bootstrap,
  showChart,
  onShowChartChange,
  onUnitChange,
  onRankChange,
  onConceptChange,
  onStatusChange,
  onContextChange,
  onBootstrapChange,
  onCurveBandToggle,
  onRandomSeedChange,
  onCurveFitMethodChange,
  onStartYearChange,
  onEndYearChange,
  onSaveSettings,
  onResetSettings,
  onShareSettings,
}) => (
  <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <SettingsIcon sx={{ mr: 1 }} />
      <Typography variant="h6">{t('parametersTitle')}</Typography>
    </Box>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth>
          <InputLabel>{t('estimationPage.unitLabel')}</InputLabel>
          <Select
            value={filters.unitFilter}
            label={t('estimationPage.unitLabel')}
            onChange={(event) => onUnitChange(event.target.value)}
          >
            {unitOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth>
          <InputLabel>{t('common:rank')}</InputLabel>
          <Select
            value={filters.rankFilter}
            label={t('common:rank')}
            onChange={(event) => onRankChange(event.target.value)}
          >
            {rankOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth>
          <InputLabel>{t('estimationPage.conceptLabel')}</InputLabel>
          <Select
            value={filters.conceptUnit}
            label={t('estimationPage.conceptLabel')}
            onChange={(event) => onConceptChange(event.target.value)}
          >
            <MenuItem value="acceptedOnly">{t('estimationPage.conceptAcceptedOnly')}</MenuItem>
            <MenuItem value="mergeSynonyms">{t('estimationPage.conceptMergeSynonyms')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth>
          <InputLabel>{t('estimationPage.contextLabel') || 'Context'}</InputLabel>
          <Select
            value={filters.contextFilter || 'neontological'}
            label={t('estimationPage.contextLabel') || 'Context'}
            onChange={(event) => onContextChange(event.target.value)}
          >
            <MenuItem value="neontological">{t('estimationPage.contextNeontological') || '现生'}</MenuItem>
            <MenuItem value="paleontological">{t('estimationPage.contextPaleontological') || '化石'}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth>
          <InputLabel>{t('estimationPage.observationLabel')}</InputLabel>
          <Select
            value={filters.statusFilter}
            label={t('estimationPage.observationLabel')}
            onChange={(event) => onStatusChange(event.target.value)}
            disabled={filters.conceptUnit === 'acceptedOnly'}
          >
            <MenuItem value="All">{t('common:all')}</MenuItem>
            <MenuItem value="Valid">{t('common:valid')}</MenuItem>
            <MenuItem value="Invalid">{t('common:invalid')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          type="number"
          label={t('estimationPage.bootstrapLabel')}
          value={bootstrap.bootstrapSamples}
          onChange={onBootstrapChange}
          helperText={t('estimationPage.bootstrapHelper')}
          inputProps={{ min: 0, max: 1000 }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={bootstrap.curveBandEnabled}
              onChange={onCurveBandToggle}
            />
          )}
          label={t('estimationPage.curveBandLabel')}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
          {t('estimationPage.curveBandHelper')}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <FormControlLabel
          control={(
            <Switch
              checked={Boolean(showChart)}
              onChange={(event) => onShowChartChange(event.target.checked)}
            />
          )}
          label={t('estimationPage.showChartToggle')}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          label={t('estimationPage.seedLabel')}
          value={bootstrap.randomSeed}
          onChange={onRandomSeedChange}
          helperText={t('estimationPage.seedHelper')}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth>
          <InputLabel>{t('estimationPage.curveFitMethodLabel')}</InputLabel>
          <Select
            value={bootstrap.curveFitMethod}
            label={t('estimationPage.curveFitMethodLabel')}
            onChange={(event) => onCurveFitMethodChange(event.target.value)}
          >
            <MenuItem value="cumulative">{t('estimationPage.curveFitMethodCumulative')}</MenuItem>
            <MenuItem value="poisson">{t('estimationPage.curveFitMethodPoisson')}</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t('estimationPage.curveFitMethodHelper')}
        </Typography>
      </Grid>
      <Grid item xs={6} sm={3} md={1.5}>
        <FormControl fullWidth>
          <InputLabel>{t('startYear')}</InputLabel>
          <Select
            value={filters.startYear}
            label={t('startYear')}
            onChange={(event) => onStartYearChange(event.target.value)}
            disabled={yearOptions.length === 0}
          >
            {yearOptions.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6} sm={3} md={1.5}>
        <FormControl fullWidth>
          <InputLabel>{t('endYear')}</InputLabel>
          <Select
            value={filters.endYear}
            label={t('endYear')}
            onChange={(event) => onEndYearChange(event.target.value)}
            disabled={yearOptions.length === 0}
          >
            {yearOptions.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>

    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" startIcon={<SaveIcon />} onClick={onSaveSettings}>{t('save')}</Button>
        <Button variant="outlined" startIcon={<RestoreIcon />} onClick={onResetSettings}>{t('reset')}</Button>
        <Button variant="outlined" startIcon={<LinkIcon />} onClick={onShareSettings}>{t('share')}</Button>
      </Stack>
    </Box>
  </Paper>
);


export default ParametersPanel;
